import { useEffect, Children, useMemo, PropsWithChildren, FC } from 'react';

import * as Sentry from '@sentry/react';
import { ErrorBoundary } from '@sentry/react';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import { ScopeProvider } from 'jotai-scope';
import useMeasure from 'react-use-measure';

import { Box } from '@ui';

import { cacheAtom } from '../hooks/useVideoCache';
import { VideoTypeByOrigin } from '../utils/consts';

import { ThreeDPlayer } from './3DPlayer';
import { MediaSizeContext } from './context';
import * as styles from './styles.css';
import { VideoPlayer } from './VideoPlayer';

type CellFrameProps = {
    children: React.ReactNode;
    top?: React.ReactNode;
    overlay?: React.ReactNode;
};

export function Composition({ children, overlay, top }: CellFrameProps) {
    const [ref, bounds] = useMeasure();
    const size = useMemo(
        () => ({ width: Math.floor(bounds.width) || 1, height: Math.floor(bounds.height) || 1 }),
        [bounds.width, bounds.height],
    );
    return (
        <ErrorBoundary
            fallback={
                <div className={styles.cell}>
                    <div className={styles.topOverlay}>media error</div>
                </div>
            }
        >
            <MediaSizeContext.Provider value={size}>
                <div className={styles.cell} ref={ref}>
                    {children}
                    {top && <div className={styles.topOverlay}>{top}</div>}
                    {overlay && <div className={styles.frameOverlay}>{overlay}</div>}
                </div>
            </MediaSizeContext.Provider>
        </ErrorBoundary>
    );
}

type MediaCellProps =
    | { type: 'video'; src: string; origin: VideoTypeByOrigin }
    | { type: 'liveView'; src: string }
    | { type: 'viewer3d'; origin: VideoTypeByOrigin; view: number }
    | {
          type: 'combined';
          src: string;
          origin: VideoTypeByOrigin;
          view: number;
      };

export const Cell = (props: MediaCellProps & Pick<CellFrameProps, 'overlay'>) => {
    switch (props.type) {
        case 'video':
            return (
                <Composition overlay={props.overlay} key={'video'}>
                    <VideoPlayer src={props.src} origin={props.origin} />
                </Composition>
            );

        case 'liveView':
            return (
                <Composition overlay={props.overlay} key={'liveView'}>
                    <img src={props.src} alt="Live View" className={styles.live} />
                </Composition>
            );

        case 'viewer3d':
            return (
                <Composition overlay={props.overlay} key={'viewer3d'}>
                    <ThreeDPlayer
                        key={props.type}
                        origin={props.origin}
                        view={props.view}
                        background={true}
                        grid={true}
                        skeleton={true}
                        lensDistortion={false}
                    />
                </Composition>
            );

        case 'combined':
            return (
                <Composition
                    key={'combined'}
                    overlay={props.overlay}
                    top={
                        <ThreeDPlayer
                            key={props.type}
                            origin={props.origin}
                            background={false}
                            grid={false}
                            skeleton={false}
                            lensDistortion={true}
                            view={props.view}
                        />
                    }
                >
                    <VideoPlayer src={props.src} origin={props.origin} />
                </Composition>
            );
    }
};

type MediaLayoutProps = {
    rows: number;
    cols: number;
    children: React.ReactNode;
};

export const MediaScope: FC<PropsWithChildren> = ({ children }) => {
    return <ScopeProvider atoms={[cacheAtom]}>{children}</ScopeProvider>;
};

export const Layout = ({ rows, cols, children }: MediaLayoutProps) => {
    const cap = rows * cols;
    const all = Children.toArray(children);
    const visible = all.slice(0, cap);
    const dropped = all.length - visible.length;

    useEffect(() => {
        if (dropped > 0) {
            Sentry.captureMessage('MediaLayout: more cells than grid slots', {
                level: 'warning',
                extra: { rows, cols, cap, provided: all.length, dropped },
            });
        }
    }, [dropped, rows, cols, cap, all.length]);

    return (
        <MediaScope>
            <Box
                className={styles.grid}
                style={assignInlineVars({
                    [styles.colsVar]: `repeat(${cols}, 1fr)`,
                    [styles.rowsVar]: `repeat(${rows}, 1fr)`,
                })}
            >
                {visible}
            </Box>
        </MediaScope>
    );
};

export const Media = {
    Cell,
    Layout,
} as const;
